/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RunTestCampaign.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Button, Paper, Divider, Chip } from "@mui/material";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import testCasesActions from "../../../redux/actions/testCasesActions";
import { HandleApiActions } from "../../../redux/actions";
import { CustomFilesUploadButton } from "../../../components";
import customConsole from "../../../config/customConsole";
import { toast } from "react-toastify";
import ViewFilesDialog from "./ViewFilesDialog";
import AddButtonV2 from "../../../components/customComponentsV2/common/button/AddButtonV2";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomDataGridV2 from "../../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";

export default function RunTestCampaign({ onCampaignRefresh }) {
  // Dispatch
  const dispatch = useDispatch();
  // Params
  const { id } = useParams();
  const { search } = useLocation();
  const paramsRunId = useMemo(() => {
    return new URLSearchParams(search).get("run_id");
  }, [search]);

  // Campaign run details
  const [campaignRunDetails, setCampaignRunDetails] = useState(null);

  const effectiveRunId = paramsRunId || campaignRunDetails?.run_id;
  // Campaign test cases list
  const [campaignTestCasesList, setCampaignTestCasesList] = useState([]);

  // Selected pull latest
  const [isSelectedPullLatest, setIsSelectedPullLatest] = useState(false);

  // Dialogs
  const [openViewFilesDialog, setOpenViewFilesDialog] = useState(false);

  // Selected test case data
  const [selectedTestCaseData, setSelectedTestCaseData] = useState(null);

  // Table
  const [tableRowsData, setTableRowsData] = useState([]);
  const [hasActiveRun, setHasActiveRun] = useState(true);
  const [isRunPendingSubmission, setIsRunPendingSubmission] = useState(false);
  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Campaign result test cases summary
  const [campaignResultTestCasesSummary, setCampaignResultTestCasesSummary] =
    useState(null);

  const isCampaignActive =
    campaignResultTestCasesSummary?.submission_type === "active";
  const campaignSubmissionType = campaignResultTestCasesSummary?.submission_type;
  let campaignStatusLabel = "-";
  if (campaignSubmissionType === "active") {
    campaignStatusLabel = "Active";
  } else if (campaignSubmissionType === "draft") {
    campaignStatusLabel = "Draft";
  } else if (campaignSubmissionType) {
    campaignStatusLabel = campaignSubmissionType;
  }

  // Handle files upload and get the uploaded file names
  const handleFilesUpload = async (files, fileType = "image") => {
    customConsole({
      message: "Selected files",
      files,
      expectedType: fileType,
    });

    const maxSize = 800 * 1024;

    // Filter valid files based on dynamic type safely
    const validFiles = Array.from(files).filter(
      (file) =>
        file &&
        typeof file.type === "string" &&
        file.type.startsWith(`${fileType}/`),
    );

    const oversizedFiles = Array.from(files).filter(
      (file) => file && typeof file.size === "number" && file.size > maxSize,
    );

    // If any file was invalid, abort upload
    if (validFiles.length !== files.length) {
      customConsole({ warning: "One or more files have invalid types." });
      return { error: true, fileNames: [] };
    }

    if (oversizedFiles.length > 0) {
      toast.error("File size should not exceed 800KB");
      return { error: true, fileNames: [] };
    }

    const uploadPromises = Array.from(files).map((file) =>
      dispatch(
        HandleApiActions({
          ...testCasesActions.upload_files,
          show_toast: true,
          file,
          is_file: true,
        }),
      ),
    );

    const results = await Promise.allSettled(uploadPromises);
    const fileNames = [];

    for (const res of results) {
      if (
        res.status === "fulfilled" &&
        res.value?.error === false &&
        res.value?.file_name
      ) {
        fileNames.push(res.value.file_name);
      } else {
        customConsole({ error: "One or more uploads failed", response: res });
        return { error: true, fileNames: [] };
      }
    }

    customConsole({ message: "All files uploaded successfully", fileNames });
    return { error: false, fileNames };
  };

  // Handle get latest campaign result test cases
  const handleGetLatestCampaignResultTestCases = useCallback(
    async ({ campaignId }) => {
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_latest_campaign_result_test_cases,
          params: {
            campaign_id: campaignId,
            page_number: page,
            page_size: rowsPerPage,
          },
        }),
      );
      if (
        resp?.error &&
        resp?.msg === "No active runs found for this campaign"
      ) {
        setHasActiveRun(false);
        toast.error(resp?.msg || "No active runs found for this campaign");
        return;
      }
      if (resp.error) {
        setCampaignTestCasesList([]);
      } else {
        setHasActiveRun(true);
        setCampaignTestCasesList(resp?.data?.results);
      }
    },
    [dispatch, page, rowsPerPage],
  );

  // Handle get campaign result test cases summary
  const handleGetCampaignResultTestCasesSummary = useCallback(
    async ({ runId }) => {
      customConsole({ runId });
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_campaign_result_test_cases,
          params: { run_id: runId },
        }),
      );
      if (resp.error) {
        customConsole({ responseError: resp.error });
        if (resp?.msg === "No active runs found for this campaign") {
          setHasActiveRun(false);
          setIsRunPendingSubmission(false);
          return false;
        }
        setCampaignResultTestCasesSummary(null);
        setCampaignTestCasesList([]);
        return false;
      } else {
        setHasActiveRun(true);
        setCampaignResultTestCasesSummary(resp?.data?.summary);
        setCampaignTestCasesList(resp?.data?.results);
        setIsRunPendingSubmission(false);
        return (resp?.data?.results || []).length > 0;
      }
    },
    [dispatch],
  );

  useEffect(() => {
    // Handle get campaign run details
    const handleGetCampaignRun = async ({ campaignId }) => {
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_campaign_run_test_cases,
          params: {
            campaign_id: campaignId,
            sort_column: "executed_at",
            sort_by: "DESC",
          },
        }),
      );
      if (resp.error) {
        setCampaignRunDetails(null);
      } else {
        const latestRun = resp?.data
          ?.slice() // create a shallow copy to avoid mutating original data
          ?.sort(
            (a, b) => new Date(b.executed_at) - new Date(a.executed_at),
          )?.[0];
        setCampaignRunDetails(latestRun);
      }
    };

    // Handle get campaign linked test cases
    const handleGetCampaignTestCases = async ({ campaignId }) => {
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_campaign_test_cases,
          params: {
            campaign_id: campaignId,
            page_number: page,
            page_size: rowsPerPage,
          },
        }),
      );
      if (resp.error) {
        setCampaignTestCasesList([]);
      } else {
        setCampaignTestCasesList(resp?.data);
      }
    };

    if (!id) return;

    const loadCampaignData = async () => {
      await handleGetCampaignRun({ campaignId: id });

      // Important: when coming from Logs with `run_id`, don't also fetch the base
      // campaign test case list; that second request overwrites the result rows.
      if (isSelectedPullLatest) {
        handleGetLatestCampaignResultTestCases({ campaignId: id });
        return;
      }

      if (paramsRunId) {
        const hasResultData = await handleGetCampaignResultTestCasesSummary({
          runId: paramsRunId,
        });
        setIsRunPendingSubmission(false);

        if (!hasResultData) {
          handleGetCampaignTestCases({ campaignId: id });
        }
        return;
      }

      handleGetCampaignTestCases({ campaignId: id });
    };

    loadCampaignData();
  }, [
    id,
    page,
    rowsPerPage,
    isSelectedPullLatest,
    paramsRunId,
    dispatch,
    handleGetCampaignResultTestCasesSummary,
    handleGetLatestCampaignResultTestCases,
  ]);

  useEffect(() => {
    if (!hasActiveRun) return;
    const filteredCampaignTestCasesList = campaignTestCasesList?.map(
      (tc, index) => {
        return {
          ...tc,
          id: (page - 1) * rowsPerPage + index + 1,
          result: tc?.result || "",
          artefact_link: tc?.artefact_link || [],
        };
      },
    );
    setTableRowsData(filteredCampaignTestCasesList);
  }, [campaignTestCasesList, page, rowsPerPage, hasActiveRun]);

  // Handle create campaign run
  const handleCreateCampaignRun = async () => {
    const getCampaignTestCasesForNewRun = async () => {
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_campaign_test_cases,
          params: {
            campaign_id: id,
            page_number: page,
            page_size: rowsPerPage,
          },
        }),
      );

      if (resp.error) {
        setCampaignTestCasesList([]);
        setTableRowsData([]);
        return;
      }

      const cleanTestCases = (resp?.data || []).map((tc) => ({
        ...tc,
        result: "",
        artefact_link: [],
      }));

      setCampaignTestCasesList(cleanTestCases);
      setTableRowsData(
        cleanTestCases.map((tc, index) => ({
          ...tc,
          id: (page - 1) * rowsPerPage + index + 1,
        })),
      );
    };

    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.create_campaign_run,
        params: { campaign_id: id },
        show_toast: true,
      }),
    );
    if (!resp.error) {
      await onCampaignRefresh?.({ campaignId: id });
      // Refresh the campaign run details after creating a new run
      const respRun = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_campaign_run_test_cases,
          params: {
            campaign_id: id,
            sort_column: "executed_at",
            sort_by: "DESC",
          },
        }),
      );
      if (!respRun.error) {
        const latestRun = respRun?.data
          ?.slice()
          ?.sort(
            (a, b) => new Date(b.executed_at) - new Date(a.executed_at),
          )?.[0];
        setCampaignRunDetails(latestRun);
        setIsRunPendingSubmission(true);
        setCampaignResultTestCasesSummary(null);
        setIsSelectedPullLatest(false);
        await getCampaignTestCasesForNewRun();
      }
    }
  };

  // Handle create result test cases
  const handleCreateResultTestCases = async ({ type }) => {
    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.add_campaign_result_test_cases,
        params: {
          run_id: effectiveRunId,
          submission_type: type,
          results: tableRowsData?.map((tc) => ({
            test_case_id: tc?.test_case_id,
            result: tc?.result,
            artefact_link: tc?.artefact_link,
          })),
        },
        show_toast: true,
      }),
    );
    if (!resp.error) {
      await onCampaignRefresh?.({ campaignId: id });
      handleGetCampaignResultTestCasesSummary({
        runId: effectiveRunId,
      });
      setIsRunPendingSubmission(false);
    }
  };

  const COLUMNS = [
    {
      field: "id",
      headerName: "S.No",
      flex: 0.4,
      minWidth: 60,
      maxWidth: 80,
      filterable: false,
      sortable: false,
    },
    {
      field: "manual_test_case_id",
      headerName: "Test Case ID",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "test_case_name",
      headerName: "Test Name",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "result",
      headerName: "Status",
      width: 150,
      renderCell: (params) => (
        <CustomSelectV2
          label=""
          required={false}
          size={"small"}
          variant={"outlined"}
          optionsList={[
            { value: "0", label: "Select" },
            { value: "passed", label: "Passed" },
            { value: "failed", label: "Failed" },
          ]}
          selectedValue={params?.row?.result || "0"}
          handleOnChange={(e) => {
            const newValue = e.target.value;
            const updatedTableRows = tableRowsData?.map((tc) => {
              if (
                tc?.manual_test_case_id === params?.row?.manual_test_case_id
              ) {
                return {
                  ...tc,
                  result: newValue,
                };
              }
              return tc;
            });

            setTableRowsData(updatedTableRows);
          }}
          sltFldStyle={{ width: "100px" }}
          disabled={!effectiveRunId}
        />
      ),
      sortable: false,
      filterable: false,
    },
    {
      field: "upload_files",
      headerName: "Upload Files",
      width: 150,
      renderCell: (params) => (
        <CustomFilesUploadButton
          size="small"
          variant="contained"
          btnName="Upload"
          multiple={true}
          accept="image/*"
          disabled={
            !(
              params?.row?.result === "passed" ||
              params?.row?.result === "failed"
            )
          }
          onFilesSelected={async (files) => {
            const filesNamesList = await handleFilesUpload(files, "image");

            if (!filesNamesList.error) {
              toast.success("Files uploaded successfully");

              const updatedTableRows = tableRowsData?.map((tc) => {
                if (
                  tc?.manual_test_case_id === params?.row?.manual_test_case_id
                ) {
                  return {
                    ...tc,
                    artefact_link: [
                      ...(tc?.artefact_link || []),
                      ...(filesNamesList.fileNames || []),
                    ],
                  };
                }
                return tc;
              });

              setTableRowsData(updatedTableRows);
            }
            customConsole({ filesNamesList });
          }}
          btnStyle={{
            ml: { xs: 1, sm: 2 },
            fontSize: { xs: "0.75rem", sm: "0.9rem" },
            py: { xs: 0.5, sm: 1 },
            px: { xs: 1, sm: 2 },
            minWidth: { xs: 40, sm: 70 },
          }}
        />
      ),
      sortable: false,
      filterable: false,
    },
    {
      field: "artefact_link",
      headerName: "View Files",
      width: 150,
      renderCell: (params) => (
        <Button
          variant="text"
          size="small"
          disabled={!params?.row?.artefact_link?.length}
          sx={{
            ml: -1,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
          onClick={() => {
            setSelectedTestCaseData({ ...params?.row });
            setOpenViewFilesDialog(true);
          }}
        >
          {"View"}
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2, height: "100%" }}>
      {/* Campaign Summary */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 0,
          pb: 2,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              gap: 2,
            }}
          >
            <Box>
              {/* Header */}
              <Typography>Campaign Summary</Typography>
            </Box>
            <Box>
              <CustomButtonV2
                size="small"
                variant="contained"
                btnName="Pull Latest Run"
                disabled={!(paramsRunId || campaignRunDetails?.run_id)}
                handleOnClick={() => {
                  setIsSelectedPullLatest(true);
                  handleGetLatestCampaignResultTestCases({ campaignId: id });
                }}
              />
            </Box>
          </Box>

          <Box>
            <AddButtonV2
              handleOnClick={handleCreateCampaignRun}
              disabled={isRunPendingSubmission}
            />
          </Box>
        </Box>

        {/* Persistent Divider */}
        <Divider sx={{ mb: 2, borderColor: "#bdbdbd" }} />

        {/* Metrics Flex Layout */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "nowrap",
            overflowX: "auto",
          }}
        >
          {[
            {
              label: "Run ID",
              value: paramsRunId || campaignRunDetails?.run_id || "-",
            },
            {
              label: "Total Test Cases",
              value: campaignResultTestCasesSummary?.total_count || "-",
            },
            {
              label: "Passed",
              value: campaignResultTestCasesSummary?.passed_count || "-",
            },
            {
              label: "Failed",
              value: campaignResultTestCasesSummary?.failed_count || "-",
            },
            {
              label: "Campaign Status",
              value: campaignStatusLabel,
            },
          ].map(({ label, value }) => (
            <Box
              key={label}
              sx={{
                flex: "1 1 auto",
                minWidth: "150px",
                minHeight: 56,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Typography fontWeight={500}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>
      {/* Footer Buttons */}
      <Box
        my={3}
        display="flex"
        justifyContent="flex-end"
        sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
      >
        <Chip
          onClick={() => handleCreateResultTestCases({ type: "draft" })}
          sx={{
            borderRadius: 0,
            color: "#007CB0",
            borderColor: "#007CB0",
            borderWidth: 1,
            fontSize: "14px",
          }}
          size="medium"
          variant="outlined"
          label={"Draft"}
          disabled={isCampaignActive || !effectiveRunId}
        />
        <CustomButtonV2
          size="small"
          variant="contained"
          btnName="Submit"
          handleOnClick={() => handleCreateResultTestCases({ type: "active" })}
          disabled={isCampaignActive || !effectiveRunId}
        />
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CustomDataGridV2
          rows={tableRowsData}
          columns={COLUMNS}
          isMultiLine={true}
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={(newPage) => setPage(newPage)}
          handlePageSizeChange={(newSize) => {
            setRowsPerPage(newSize);
            setPage(1);
          }}
        />
      </Box>

      {/* View files dialog */}
      <ViewFilesDialog
        open={openViewFilesDialog}
        onClose={() => setOpenViewFilesDialog(false)}
        dialogTitle="View Uploaded Files"
        selectedTestCaseData={selectedTestCaseData}
        setSelectedTestCaseData={setSelectedTestCaseData}
      />
    </Box>
  );
}

RunTestCampaign.propTypes = {
  onCampaignRefresh: PropTypes.func,
};
